import { CoreSelect, MMButton } from '@components'
import AddIcon from '@images/icons/new-icon.svg?react'
import { toastFailure, toastSuccess } from '@utils'
import ExpenseRecipientDetails from '@views/expenseManagement/@elements/ExpenseRecipientDetails'
import {
  useCounterpartyOptionsQry,
  useCreateCounterpartyMutation
  // useExpenseRecipientsQry
} from 'mmfintech-backend-api'
import { checkFinalValue, tr, translateError } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useCallback, useMemo, useState } from 'react'
import ReactTooltip from 'react-tooltip'
import CreateRecipient from './CreateRecipient'
import { useExpenseRecipientsQry } from './useExpenseRecipientsQry'

import './expenseRecipientTab.scss'

type ExpenseRecipientTabProps = {
  formValues: UseFormValuesOutput
}

const ExpenseRecipientTab = ({ formValues: expenseValues }: ExpenseRecipientTabProps) => {
  const foreignId = useMemo(() => {
    if (!expenseValues.getValue('reimbursement')) {
      const counterpartyId = expenseValues.getValue('counterpartyId')
      return typeof counterpartyId === 'number' ? counterpartyId : counterpartyId?.value
    }
    return null
  }, [expenseValues])

  const [createRecipientVisible, setCreateRecipientVisible] = useState<boolean>(false)

  const { counterpartyOptions, counterpartiesError, counterpartiesFetching } = useCounterpartyOptionsQry()

  const [createCounterpartyRequest, { isLoading: createCounterpartyLoading }] = useCreateCounterpartyMutation()

  const { recipients, recipientsError, recipientsFetching } = useExpenseRecipientsQry({
    foreignId,
    currencyCode: expenseValues.getValue('currency'),
    reimbursement: expenseValues.getValue('reimbursement'),
    pageSize: 10000
  })

  const selectedRecipient = useMemo(() => {
    const recipientId = Number(expenseValues.getValue('recipientId'))
    return recipientId ? recipients?.find((entry: any) => Number(entry.paymentInstrumentId) === recipientId) : null
  }, [recipients, expenseValues])

  const findRecipientName = useCallback(
    (id: string | number): string => {
      const recipientId = Number(id)
      return recipients?.find(x => x.paymentInstrumentId === recipientId)?.name ?? ''
    },
    [recipients]
  )

  const handleCreateCounterparty = async (name: string) => {
    const check = checkFinalValue(name, { validation: 'safe-string' })
    if (!check.valid) {
      toastFailure(translateError(check))
      return
    }

    try {
      const res: any = await createCounterpartyRequest({ name }).unwrap()
      expenseValues.setValue('counterpartyId', res?.id)
      toastSuccess(tr('FRONTEND.EXPENSE_MODAL.COUNTERPARTY.CREATE_SUCCESS', 'Counterparty created successfully'))
    } catch (err) {
      toastFailure(err?.cause?.[0]?.cause || err.error)
    }
  }

  return (
    <div className='create-expense-recipient-wrapper' data-test='expense-recipient-tab'>
      <div className='group-inputs'>
        <div
          {...(expenseValues.getValue('reimbursement')
            ? {
                'data-for': 'counterparty-tooltip',
                'data-tip': tr(
                  'FRONTEND.EXPENSE_MODAL.COUNTERPARTY_DISABLED_TOOLTIP',
                  'As Reimbursement is selected on the previous tab, only personal accounts are available here.'
                )
              }
            : null)}
        >
          <CoreSelect
            type='default'
            clearLabel={
              expenseValues.getValue('counterpartyId') ? tr('FRONTEND.COMMON.CLEAR_SELECTION', 'Clear selection') : null
            }
            loading={counterpartiesFetching}
            disabled={Boolean(expenseValues.getValue('reimbursement')) || createCounterpartyLoading}
            options={counterpartyOptions}
            enablePrefillOption={false}
            label={tr('FRONTEND.EXPENSE_MODAL.COUNTERPARTY.LABEL', 'Counterparty')}
            value={expenseValues.getValue('counterpartyId')}
            error={expenseValues.getError('counterpartyId')}
            required={expenseValues.isRequired('counterpartyId')}
            onChange={(_, value) => {
              expenseValues.setValue('counterpartyId', value)
              expenseValues.setValue('recipientId', '')
              expenseValues.setValue('recipientName', '')
              setCreateRecipientVisible(false)
            }}
            CreateComponent={({ filterString }: { filterString: string }) => {
              return filterString &&
                !counterpartyOptions.some(({ label }) => label.toLowerCase() === filterString.toLowerCase()) ? (
                <MMButton
                  loading={createCounterpartyLoading}
                  className='create-button'
                  variant='secondary'
                  onClick={() => handleCreateCounterparty(filterString)}
                  caption={
                    <div className='create-content'>
                      <div>
                        <AddIcon /> {filterString}
                      </div>
                      <span className='create-label'>
                        {tr('FRONTEND.EXPENSE_MODAL.COUNTERPARTY.CREATE_BUTTON', 'Create')}
                      </span>
                    </div>
                  }
                  size='normal'
                  fullWidth
                />
              ) : (
                <span className='create-content hint'>
                  <AddIcon /> {tr('FRONTEND.EXPENSE_MODAL.COUNTERPARTY.HINT', 'Start typing to search or create new')}
                </span>
              )
            }}
          />
        </div>

        <CoreSelect
          className='recipient-select'
          type='default'
          clearLabel={
            expenseValues.getValue('recipientId') ? tr('FRONTEND.COMMON.CLEAR_SELECTION', 'Clear selection') : null
          }
          data-test='recipient'
          label={tr('FRONTEND.EXPENSE_MODAL.RECIPIENT.LABEL', 'Recipient')}
          value={expenseValues.getValue('recipientId')}
          options={
            recipients?.map((recipient: any) => ({
              value: recipient.paymentInstrumentId,
              label: recipient.name
            })) || []
          }
          enablePrefillOption={false}
          isAccountsLoading={recipientsFetching}
          CreateComponent={() => {
            return createRecipientVisible || recipientsFetching ? null : (
              <div
                className='create-recipient-button'
                onClick={() => {
                  expenseValues.setValue('recipientId', '')
                  expenseValues.setValue('recipientName', '')
                  setCreateRecipientVisible(true)
                }}
              >
                {tr('FRONTEND.EXPENSE_MODAL.RECIPIENT.CREATE_BUTTON', 'Create new recipient')}
              </div>
            )
          }}
          onChange={(_, value) => {
            expenseValues.setValue('recipientId', value)
            expenseValues.setValue('recipientName', findRecipientName(value))
            setCreateRecipientVisible(false)
            // setCountryCode(recipients?.find(x => x.paymentInstrumentId === value)?.country || null)
          }}
        />
      </div>
      {Boolean(expenseValues.getValue('reimbursement')) && (
        <ReactTooltip id='counterparty-tooltip' padding='8px 6px' place='bottom' html={true} aria-haspopup='true' />
      )}

      {createRecipientVisible ? (
        <CreateRecipient
          foreignId={foreignId}
          personal={expenseValues.getValue('reimbursement')}
          currencyCode={expenseValues.getValue('currency')}
          onClose={() => setCreateRecipientVisible(false)}
          onSuccess={recipientId => {
            expenseValues.setValue('recipientId', recipientId)
            expenseValues.setValue('recipientName', findRecipientName(recipientId))
          }}
        />
      ) : (
        <ExpenseRecipientDetails recipient={selectedRecipient} />
      )}

      <ErrorDisplay error={[counterpartiesError, recipientsError]} />
    </div>
  )
}

export default ExpenseRecipientTab
